/**
 * Boards API (M2 canonical) — typed wrappers over
 *   /api/orgs/:orgId/projects/:projectId/boards
 *   /api/boards/:boardId
 *   /api/boards/:boardId/columns
 *
 * Milestone 2.4, spec §3.6. Mirrors
 * apps/chaos-gateway/src/projects-boards.ts.
 *
 * IMPORTANT: this is the v2 boards surface (chaos.boards table). The
 * legacy /api/kanban/boards/* surface still exists for backwards
 * compatibility and is wrapped by kanban.ts.
 */
import type {
  CreateOrgScopedBoardInput,
  CreateOrgScopedColumnInput,
  OrgScopedBoard,
  OrgScopedColumn,
  ReorderOrgScopedBoardsInput,
  ReorderOrgScopedColumnsInput,
  UpdateOrgScopedBoardInput,
  UpdateOrgScopedColumnInput,
} from '@chaos/types/projects-boards';

import { apiJson } from './client';

interface BoardsListResponse {
  boards: OrgScopedBoard[];
}
interface BoardResponse {
  board: OrgScopedBoard;
}
interface ColumnsListResponse {
  columns: OrgScopedColumn[];
}
interface ColumnResponse {
  column: OrgScopedColumn;
}

export const boardsApi = {
  // ── Boards under a project ─────────────────────────────────────────
  list(orgId: string, projectId: string, includeArchived = false): Promise<OrgScopedBoard[]> {
    const qs = includeArchived ? '?archived=true' : '';
    return apiJson<BoardsListResponse>(
      `/orgs/${orgId}/projects/${projectId}/boards${qs}`,
    ).then((r) => r.boards);
  },

  /**
   * Resolve an M2 board from a legacy `boardId` URL parameter. Tries
   * the direct id first; on 404 falls back to "first board of the
   * project whose id matches the given uuid". This bridges the legacy
   * `/board/:projectId` URL convention to M2 `chaos.boards`.
   */
  async resolveByLegacy(orgId: string, legacyId: string): Promise<OrgScopedBoard | null> {
    try {
      return await this.get(legacyId);
    } catch (err) {
      // not_found → try project mapping
    }
    try {
      const boards = await this.list(orgId, legacyId);
      return boards[0] ?? null;
    } catch {
      return null;
    }
  },
  create(
    orgId: string,
    projectId: string,
    input: CreateOrgScopedBoardInput,
  ): Promise<OrgScopedBoard> {
    return apiJson<BoardResponse>(`/orgs/${orgId}/projects/${projectId}/boards`, {
      method: 'POST',
      json: input,
    }).then((r) => r.board);
  },

  // Settings V2 (0064) — reorder boards within a project (1-based positions).
  reorder(
    orgId: string,
    projectId: string,
    input: ReorderOrgScopedBoardsInput,
  ): Promise<OrgScopedBoard[]> {
    return apiJson<BoardsListResponse>(
      `/orgs/${orgId}/projects/${projectId}/boards/reorder`,
      { method: 'POST', json: input },
    ).then((r) => r.boards);
  },

  // ── Board by id ────────────────────────────────────────────────────
  get(boardId: string): Promise<OrgScopedBoard> {
    return apiJson<BoardResponse>(`/boards/${boardId}`).then((r) => r.board);
  },
  update(boardId: string, input: UpdateOrgScopedBoardInput): Promise<OrgScopedBoard> {
    return apiJson<BoardResponse>(`/boards/${boardId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.board);
  },
  archive(boardId: string): Promise<OrgScopedBoard> {
    return apiJson<BoardResponse>(`/boards/${boardId}`, { method: 'DELETE' }).then((r) => r.board);
  },
  // Unified Trash (TRASH_ENABLED) — un-archive a board (404 when gateway flag OFF).
  restore(boardId: string): Promise<{ ok: true; id: string }> {
    return apiJson<{ ok: true; id: string }>(`/boards/${boardId}/restore`, { method: 'POST' });
  },
  // Unified Trash (TRASH_ENABLED) — PERMANENTLY delete an archived board
  // (gateway tears down any contained session containers first). Founder/admin.
  purge(boardId: string): Promise<{ ok: true; id: string; containersRemoved: number }> {
    return apiJson<{ ok: true; id: string; containersRemoved: number }>(`/boards/${boardId}/purge`, {
      method: 'DELETE',
    });
  },

  // ── Columns under a board ──────────────────────────────────────────
  listColumns(boardId: string): Promise<OrgScopedColumn[]> {
    return apiJson<ColumnsListResponse>(`/boards/${boardId}/columns`).then((r) => r.columns);
  },
  createColumn(boardId: string, input: CreateOrgScopedColumnInput): Promise<OrgScopedColumn> {
    return apiJson<ColumnResponse>(`/boards/${boardId}/columns`, {
      method: 'POST',
      json: input,
    }).then((r) => r.column);
  },
  updateColumn(
    boardId: string,
    columnId: string,
    input: UpdateOrgScopedColumnInput,
  ): Promise<OrgScopedColumn> {
    return apiJson<ColumnResponse>(`/boards/${boardId}/columns/${columnId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.column);
  },
  archiveColumn(boardId: string, columnId: string): Promise<OrgScopedColumn> {
    return apiJson<ColumnResponse>(`/boards/${boardId}/columns/${columnId}`, {
      method: 'DELETE',
    }).then((r) => r.column);
  },
  reorderColumns(
    boardId: string,
    input: ReorderOrgScopedColumnsInput,
  ): Promise<OrgScopedColumn[]> {
    return apiJson<ColumnsListResponse>(`/boards/${boardId}/columns/reorder`, {
      method: 'POST',
      json: input,
    }).then((r) => r.columns);
  },
};

export const boardsKeys = {
  all: ['boards-v2'] as const,
  listInProject: (orgId: string, projectId: string, includeArchived = false) =>
    [...boardsKeys.all, 'list', orgId, projectId, includeArchived] as const,
  detail: (boardId: string) => [...boardsKeys.all, 'detail', boardId] as const,
  columns: (boardId: string) => [...boardsKeys.all, 'columns', boardId] as const,
};
